/**
 * Promise-based confirm() replacement.
 *
 * Fixes site-wide dead upgrade buttons: `window.confirm()` is blocked/no-op
 * inside the embedded preview iframe and in some mobile shells, so clicks
 * that relied on it did nothing. This mounts a real in-app modal and
 * returns a promise that resolves true/false.
 *
 * Usage:
 *   import { confirmDialog } from "@/components/ui/confirm-dialog";
 *   const ok = await confirmDialog({ title: "Upgrade?", message: "Cost 500 Chits." });
 *   if (!ok) return;
 *
 * Mount `<ConfirmDialogPortal />` ONCE near the root of the app.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

let _resolver = null;
let _setOpen = null;
let _setPayload = null;

export function confirmDialog({ title = "Confirm", message = "", confirmLabel = "Confirm", cancelLabel = "Cancel", tone = "default" } = {}) {
  return new Promise((resolve) => {
    if (!_setOpen) {
      // Portal not mounted yet — fall back to window.confirm so we never hard-hang.
      // (Should never happen in practice — the portal is in App.js root.)
      const msg = title + (message ? `\n\n${message}` : "");
      resolve(!!window.confirm(msg));
      return;
    }
    _resolver = resolve;
    _setPayload({ title, message, confirmLabel, cancelLabel, tone });
    _setOpen(true);
  });
}

export default function ConfirmDialogPortal() {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState({
    title: "", message: "", confirmLabel: "Confirm", cancelLabel: "Cancel", tone: "default",
  });
  const location = useLocation();

  useEffect(() => {
    _setOpen = setOpen;
    _setPayload = setPayload;
    return () => { _setOpen = null; _setPayload = null; };
  }, []);

  // 🚨 Roc support (Sep 2026): auto-close + resolve false on any route change
  // so a dialog never gets "stuck open" across pages (user reported overlays
  // blocking clicks site-wide, including the Guild Leave button).
  useEffect(() => {
    if (_resolver) {
      const r = _resolver;
      _resolver = null;
      try { r(false); } catch { /* silent */ }
    }
    setOpen(false);
  }, [location.pathname]);

  const close = (result) => {
    setOpen(false);
    if (_resolver) {
      const r = _resolver;
      _resolver = null;
      r(result);
    }
  };

  // 🚨 Escape key always closes the dialog (resolves false) — mobile users
  // without a visible keyboard still get a route-change / overlay-tap fallback.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const btnBase = "px-4 py-2 rounded-md font-bold text-sm transition";
  const confirmBg = payload.tone === "danger"
    ? "bg-red-600 hover:bg-red-500 text-white"
    : "bg-amber-500 hover:bg-amber-400 text-black";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="confirm-dialog-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) close(false); }}
    >
      <div
        className="max-w-md w-full rounded-xl border border-amber-700/40 bg-neutral-950 shadow-2xl p-6"
        data-testid="confirm-dialog"
        role="dialog"
        aria-modal="true"
      >
        {payload.title && (
          <div className="text-lg font-black text-amber-200 mb-2" data-testid="confirm-dialog-title">
            {payload.title}
          </div>
        )}
        {payload.message && (
          <div className="text-sm text-neutral-300 whitespace-pre-line mb-5" data-testid="confirm-dialog-message">
            {payload.message}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => close(false)}
            className={`${btnBase} bg-neutral-800 hover:bg-neutral-700 text-neutral-200`}
            data-testid="confirm-dialog-cancel"
          >
            {payload.cancelLabel || "Cancel"}
          </button>
          <button
            type="button"
            onClick={() => close(true)}
            className={`${btnBase} ${confirmBg}`}
            data-testid="confirm-dialog-confirm"
            autoFocus
          >
            {payload.confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
